import { Constants } from "./constants";

/**
 * Show types where each dog picks its own time slot per class at registration
 * (General sanctioning club "Show N Go" formats). Run orders for these shows keep
 * the judge-schedule class structure but list dogs in time-slot order instead of
 * armband order.
 */
export const PER_CLASS_TIMESLOT_SHOW_TYPES: string[] = [
  Constants.show_type.obedience_show_n_go,
  Constants.show_type.general_rally,
];

export const isPerClassTimeslotShow = (sanctioningClub?: string, showType?: string): boolean =>
  sanctioningClub === Constants.sanctioning_club.GENERAL &&
  !!showType &&
  PER_CLASS_TIMESLOT_SHOW_TYPES.includes(showType);

/**
 * Parse the start time out of a time-slot label into minutes from midnight.
 * Handles "9:00 AM - 9:10 AM", "9:00 AM", "09:00", "9am", "1:30pm" etc.
 * Returns undefined when no time can be found (free-form labels).
 */
export const timeSlotToMinutes = (label?: string | null): number | undefined => {
  if (!label) return undefined;
  const pattern = /(\d{1,2})(?::(\d{2}))?\s*([AaPp])?\.?[Mm]?\.?(?![\d:])/g;

  let match: RegExpExecArray | null;
  while ((match = pattern.exec(label)) !== null) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2] ? parseInt(match[2], 10) : 0;
    const meridiem = match[3]?.toLowerCase();

    // Bare numbers with no ":" and no am/pm aren't reliable as times; keep scanning.
    if (!match[2] && !meridiem) continue;
    if (Number.isNaN(hours) || hours > 24 || minutes > 59) continue;

    if (meridiem === "p" && hours < 12) hours += 12;
    if (meridiem === "a" && hours === 12) hours = 0;

    return hours * 60 + minutes;
  }

  return undefined;
};

/**
 * Compare two time-slot labels chronologically. Labels without a parseable time
 * sort after those with one (alphabetically among themselves); missing labels last.
 */
export const compareTimeSlots = (a?: string | null, b?: string | null): number => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  const aMin = timeSlotToMinutes(a);
  const bMin = timeSlotToMinutes(b);

  if (aMin !== undefined && bMin !== undefined && aMin !== bMin) return aMin - bMin;
  if (aMin !== undefined && bMin === undefined) return -1;
  if (aMin === undefined && bMin !== undefined) return 1;

  return a.localeCompare(b);
};
